import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Chip,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Alert,
  Badge,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Remove as RemoveIcon,
  ContentCopy as CopyIcon,
  ViewList as ViewListIcon,
  ViewKanban as ViewKanbanIcon,
} from "@mui/icons-material";
import { RootState, AppDispatch } from "../store";
import {
  fetchCases,
  createCase,
  updateCase,
  deleteCase,
} from "../store/slices/casesSlice";
import { fetchClients } from "../store/slices/clientsSlice";
import { Case, CaseCreateInput } from "../services/casesService";
import usersService from "../services/usersService";
import {
  generatePublicAccessCode,
  generatePublicAccessLink,
} from "../utils/passwordGenerator";
import ProformaQuotation from "../components/ProformaQuotation";
import { useToast } from "../hooks/useToast";
import { getApiErrorMessage, getApiFieldErrors } from "../utils/apiError";
import StatusBadge, { getStatusFilterOptions } from "../components/common/StatusBadge";
import { getCaseProgressFromStatus } from "../utils/caseProgress";
import CasesKanban from "../components/Cases/CasesKanban";
import { useConfirm } from "../contexts/ConfirmDialogContext";

interface LawyerAssignment {
  lawyer_id: string;
  lawyer_name: string;
  fee: string;
}

interface FormData {
  customer_id: number;
  lawyers_assigned: LawyerAssignment[];
  supervisor: string;
  type: string;
  subject: string;
  start_date: string;
  end_date: string;
  billing_type: string;
  fee: string;
  supervisor_fee: string;
  fixed_fee: string;
  currency: string;
  status: string;
  priority: string;
  enable_public_access: boolean;
  public_access_code?: string;
}

const initialFormData: FormData = {
  customer_id: 0,
  lawyers_assigned: [],
  supervisor: "",
  type: "",
  subject: "",
  start_date: "",
  end_date: "",
  billing_type: "",
  fee: "",
  supervisor_fee: "",
  fixed_fee: "",
  currency: "USD",
  status: "pending",
  priority: "medium",
  enable_public_access: false,
};

const Cases = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const confirm = useConfirm();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { cases, loading } = useSelector((state: RootState) => state.cases);
  const { clients } = useSelector((state: RootState) => state.clients);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCases, setFilteredCases] = useState<Case[]>([]);
  const [dialogTab, setDialogTab] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [publicAccessLink, setPublicAccessLink] = useState<string>("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const { showSuccess, showError } = useToast();

  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Replace with actual user ID
    const userId = user?.user_id || "";
    if (userId) {
      dispatch(fetchCases({ userId }));
      dispatch(fetchClients(userId));
    }
    loadUsers();
  }, [dispatch, user?.user_id]);

  // Generate public access link when editing an existing case with public access enabled
  useEffect(() => {
    if (
      selectedCase &&
      formData.enable_public_access &&
      formData.public_access_code &&
      user?.firm_id
    ) {
      const link = generatePublicAccessLink(
        selectedCase.id,
        user.firm_id,
        formData.public_access_code,
      );
      setPublicAccessLink(link);
    } else {
      setPublicAccessLink("");
    }
  }, [
    selectedCase,
    formData.enable_public_access,
    formData.public_access_code,
    user?.firm_id,
  ]);

  const loadUsers = async () => {
    try {
      const usersData = await usersService.getUsers();
      setUsers(usersData.data);
    } catch (err: any) {
      console.error("Failed to load users:", err);
    }
  };

  useEffect(() => {
    let result = cases;

    if (statusFilter !== "all") {
      result = result.filter(
        (case_) => (case_?.status || "").toLowerCase() === statusFilter,
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((case_) => {
        const client = clients.find((c) => c.id === case_?.customer_id);
        const clientNames = client
          ? [client.company || ""].join(" ").toLowerCase()
          : "";

        return (
          case_?.subject?.toLowerCase().includes(query) ||
          case_?.type?.toLowerCase().includes(query) ||
          (case_?.lawyers_assigned &&
            (typeof case_.lawyers_assigned === "string"
              ? case_.lawyers_assigned.toLowerCase().includes(query)
              : case_.lawyers_assigned
                  .join(", ")
                  .toLowerCase()
                  .includes(query))) ||
          case_?.supervisor?.full_name?.toLowerCase().includes(query) ||
          clientNames.includes(query) ||
          case_?.status?.toLowerCase().includes(query)
        );
      });
    }

    setFilteredCases(result);
  }, [searchQuery, statusFilter, cases, clients]);

  const handleOpenDialog = (caseData?: Case) => {
    if (caseData) {
      setSelectedCase(caseData);
      // Parse existing lawyers_assigned string into array
      let lawyersArray: LawyerAssignment[] = [];
      if (
        typeof caseData.lawyers_assigned === "string" &&
        caseData.lawyers_assigned
      ) {
        const lawyerEntries = caseData.lawyers_assigned
          .split(",")
          .map((entry) => entry.trim());

        lawyersArray = lawyerEntries.map((entry) => {
          // Match "user_id: fee" format (hourly billing)
          const match = entry.match(/^([^:]+):\s*(\d+(?:\.\d+)?)$/);
          if (match) {
            const lawyerId = match[1].trim();
            const fee = match[2];
            // Use lawyers_list (always available on caseData) for name lookup
            const lawyerFromList = caseData.lawyers_list?.find(
              (l: any) => l.user_id === lawyerId,
            );
            return {
              lawyer_id: lawyerId,
              lawyer_name: lawyerFromList?.full_name || "",
              fee,
            };
          }

          // Fixed billing: could be plain user_id OR full name
          // Try matching by user_id in lawyers_list first
          const lawyerById = caseData.lawyers_list?.find(
            (l: any) => l.user_id === entry,
          );
          if (lawyerById) {
            return {
              lawyer_id: lawyerById.user_id,
              lawyer_name: lawyerById.full_name,
              fee: "0",
            };
          }
          // Fall back to matching by full_name in lawyers_list
          const lawyerByName = caseData.lawyers_list?.find(
            (l: any) => l.full_name === entry,
          );
          if (lawyerByName) {
            return {
              lawyer_id: lawyerByName.user_id,
              lawyer_name: lawyerByName.full_name,
              fee: "0",
            };
          }
          return { lawyer_id: entry, lawyer_name: entry, fee: "0" };
        });
      }

      // Supervisor: caseData.supervisor can be a string (name) or object with user_id
      let supervisorUserId = "";
      if (
        typeof caseData.supervisor === "object" &&
        caseData.supervisor?.user_id
      ) {
        supervisorUserId = caseData.supervisor.user_id;
      } else if (typeof caseData.supervisor === "string") {
        // API returned a name — won't reliably resolve without users loaded,
        // but try anyway as fallback
        const supervisorUser = users.find(
          (u: any) => u.full_name === caseData.supervisor,
        );
        supervisorUserId = supervisorUser?.user_id || "";
      }

      // Format dates for date inputs (YYYY-MM-DD)
      const formatDateForInput = (dateString: string | null) => {
        if (!dateString) return "";
        // Handle format like "2026-03-24 00:00:00"
        return dateString.split(" ")[0];
      };

      console.log("Parsed lawyersArray:", lawyersArray);
      console.log("Original lawyers_assigned:", caseData.lawyers_assigned);
      console.log("Supervisor user_id:", supervisorUserId);
      console.log("Full caseData.supervisor object:", caseData.supervisor);
      console.log("Available users for dropdown:", users);

      setFormData({
        customer_id: caseData.customer_id,
        lawyers_assigned: lawyersArray,
        supervisor: supervisorUserId,
        type: caseData.type,
        subject: caseData.subject,
        start_date: formatDateForInput(caseData.start_date),
        end_date: formatDateForInput(caseData.end_date),
        billing_type: caseData.billing_type,
        fee: caseData.fee.toString(),
        supervisor_fee: caseData.supervisor_fee.toString(),
        fixed_fee: (
          caseData.fixed_fee ||
          (caseData.billing_type === "fixed" ? caseData.fee : "") ||
          ""
        ).toString(),
        currency: caseData.currency,
        status: caseData.status,
        priority: caseData.priority || "medium",
        enable_public_access: caseData.enable_public_access || false,
        public_access_code: caseData.public_access_code || "",
      });
    } else {
      setSelectedCase(null);
      setFormData(initialFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCase(null);
    setFormData(initialFormData);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate required fields
    if (!formData.customer_id) {
      errors.customer_id = t("validation.required");
    }

    if (!formData.type) {
      errors.type = t("validation.required");
    }

    if (!formData.subject.trim()) {
      errors.subject = t("validation.required");
    }

    if (!formData.billing_type) {
      errors.billing_type = t("validation.required");
    }

    // A case may be staffed by a supervisor alone, a lawyer alone, both,
    // or neither yet at creation time — neither is mandatory.

    // Validate fee based on billing type
    if (formData.billing_type === "fixed") {
      if (!formData.fixed_fee || parseFloat(formData.fixed_fee) <= 0) {
        errors.fixed_fee = t("validation.required");
      }
    } else if (formData.billing_type === "hourly") {
      // Supervisor fee only matters (and is only required) once a
      // supervisor is actually assigned to bill against.
      if (
        formData.supervisor &&
        (!formData.supervisor_fee || parseFloat(formData.supervisor_fee) < 0)
      ) {
        errors.supervisor_fee = t("validation.required");
      }
      // Any lawyer entries that are assigned must have a valid fee, but
      // having no lawyers assigned at all is allowed.
      const invalidLawyerFees = formData.lawyers_assigned.some(
        (lawyer) => !lawyer.fee || parseFloat(lawyer.fee) < 0,
      );
      if (invalidLawyerFees) {
        errors.lawyers_assigned = t("validation.required");
      }
    }

    // Any lawyer entries present must at least identify a lawyer.
    if (formData.lawyers_assigned.some((lawyer) => !lawyer.lawyer_id)) {
      errors.lawyers_assigned = t("validation.required");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showError(t("validation.formHasErrors"));
      return;
    }

    const userId = user?.user_id || ""; // Replace with actual user ID
    // Convert lawyers_assigned array based on billing type
    let lawyersAssignedValue;
    if (formData.billing_type === "fixed") {
      // For fixed billing, send all lawyer IDs as comma-separated values
      lawyersAssignedValue = formData.lawyers_assigned
        .filter((lawyer) => lawyer.lawyer_id)
        .map((lawyer) => lawyer.lawyer_id)
        .join(", ");
    } else {
      // For hourly billing, send array of user_id: fee pairs
      lawyersAssignedValue = formData.lawyers_assigned
        .filter((lawyer) => lawyer.lawyer_id && lawyer.fee)
        .map((lawyer) => `${lawyer.lawyer_id}: ${lawyer.fee}`);
    }

    const caseData: CaseCreateInput = {
      customer_id: formData.customer_id,
      lawyers_assigned: Array.isArray(lawyersAssignedValue)
        ? lawyersAssignedValue.join(", ")
        : lawyersAssignedValue,
      supervisor: formData.supervisor,
      type: formData.type,
      subject: formData.subject,
      start_date: formData.start_date,
      end_date: formData.end_date,
      billing_type: formData.billing_type,
      fee:
        formData.billing_type === "fixed" ? parseFloat(formData.fixed_fee) : 0,
      supervisor_fee:
        formData.billing_type === "hourly"
          ? parseFloat(formData.supervisor_fee)
          : 0,
      fixed_fee:
        formData.billing_type === "fixed"
          ? parseFloat(formData.fixed_fee)
          : undefined,
      currency: formData.currency,
      status: formData.status,
      priority: formData.priority,
      user_id: userId,
      enable_public_access: formData.enable_public_access,
      public_access_code: formData.enable_public_access
        ? formData.public_access_code
        : undefined,
    };

    try {
      if (selectedCase) {
        await dispatch(
          updateCase({
            id: selectedCase.id,
            ...caseData,
          }),
        ).unwrap();
      } else {
        const result = await dispatch(createCase(caseData)).unwrap();

        // Generate public access link if case was created successfully and public access is enabled
        if (formData.enable_public_access && formData.public_access_code) {
          const newCaseId = (result as any)?.data?.id;
          if (newCaseId && user?.firm_id) {
            const link = generatePublicAccessLink(
              newCaseId,
              user.firm_id,
              formData.public_access_code,
            );
            setPublicAccessLink(link);
          }
        }
      }
      showSuccess(t("cases.caseSaved"));
      dispatch(fetchCases({ userId }));
      handleCloseDialog();
    } catch (error: any) {
      // A 422 comes back as `{ errors: { subject: [...] } }` — surface it on
      // the actual field as well as in the toast, instead of just a generic
      // "Failed to save case" message (or, previously, showing this error
      // and *then* an unconditional "Case saved successfully" toast right
      // after it).
      const fieldErrors = getApiFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
      showError(getApiErrorMessage(error, t("cases.errorSavingCase", "Failed to save case")));
    }
  };

  const copyPublicAccessLink = async () => {
    if (publicAccessLink) {
      try {
        await navigator.clipboard.writeText(publicAccessLink);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy link:", err);
      }
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm(t("cases.confirmDeleteCase"));
    if (ok) {
      const userId = "1"; // Replace with actual user ID
      try {
        await dispatch(deleteCase({ orderId: id.toString(), userId })).unwrap();
        showSuccess(t("cases.caseDeleted"));
      } catch (error: any) {
        showError(getApiErrorMessage(error, t("cases.errorDeletingCase", "Failed to delete case")));
      }
    }
  };

  const addLawyerAssignment = () => {
    setFormData({
      ...formData,
      lawyers_assigned: [
        ...formData.lawyers_assigned,
        { lawyer_id: "", lawyer_name: "", fee: "0" },
      ],
    });
  };

  const removeLawyerAssignment = (index: number) => {
    setFormData({
      ...formData,
      lawyers_assigned: formData.lawyers_assigned.filter((_, i) => i !== index),
    });
  };

  const updateLawyerAssignment = (
    index: number,
    field: keyof LawyerAssignment,
    value: string,
  ) => {
    console.log("updateLawyerAssignment[value]", value);
    const updatedLawyers = [...formData.lawyers_assigned];
    updatedLawyers[index] = { ...updatedLawyers[index], [field]: value };

    // If updating lawyer_id, also update lawyer_name
    if (field === "lawyer_id") {
      const selectedUser = users.find((user) => user.user_id === value);
      if (selectedUser) {
        updatedLawyers[index].lawyer_name = selectedUser.full_name;
      }
    }

    setFormData({ ...formData, lawyers_assigned: updatedLawyers });
    if (formErrors.lawyers_assigned) {
      setFormErrors((prev) => ({ ...prev, lawyers_assigned: "" }));
    }
  };

  const handlePublicAccessToggle = (enabled: boolean) => {
    console.log("enabled", enabled);
    setFormData({ ...formData, enable_public_access: enabled });

    if (enabled && !formData.public_access_code) {
      const accessCode = generatePublicAccessCode();
      setFormData({
        ...formData,
        enable_public_access: enabled,
        public_access_code: accessCode,
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: "customer",
      headerName: t("cases.client"),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const client = clients.find((c) => c.id === params.row.customer_id);
        return client ? `${client.company}` : t("cases.unknownClient");
      },
    },
    {
      field: "lawyers_assigned",
      headerName: t("cases.lawyersAssigned"),
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const names = params.row.lawyers_list
          ?.map((l: any) => l.full_name)
          .join(", ");
        return names || params.row.lawyers_assigned;
      },
    },
    { field: "supervisor", headerName: t("cases.supervisor"), width: 150 },
    { field: "type", headerName: t("cases.caseType"), width: 150 },
    {
      field: "start_date",
      headerName: t("cases.startDate"),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
      },
    },
    {
      field: "fee",
      headerName: t("cases.fee"),
      width: 100,
      type: "number",
      renderCell: (params: GridRenderCellParams) => (
        <Badge>
          {getCurrencySymbol(params.row.currency)}
          {params.row.total_charges}
        </Badge>
      ),
    },
    {
      field: "files_count",
      headerName: t("common.files"),
      width: 100,
    },
    {
      field: "total_time_spent",
      headerName: t("cases.timeSpent"),
      width: 150,
      type: "number",
    },
    {
      field: "status",
      headerName: t("common.status"),
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <StatusBadge status={params.row.status} />
      ),
    },
    {
      field: "priority",
      headerName: t("cases.priority", "Priority"),
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const priority = (params.row.priority || "medium") as
          | "low"
          | "medium"
          | "high";
        const colorMap: Record<string, "default" | "warning" | "error"> = {
          low: "default",
          medium: "warning",
          high: "error",
        };
        return (
          <Chip
            label={t(`common.${priority}`)}
            size="small"
            color={colorMap[priority] || "default"}
            variant="outlined"
          />
        );
      },
    },
    {
      field: "progress",
      headerName: t("cases.progress", "Progress"),
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const progress = getCaseProgressFromStatus(params.row.status);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ flex: 1, height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary">
              {progress}%
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: t("common.actions"),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t("common.edit")}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(params.row);
              }}
              size="small"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.row.id);
              }}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      INR: "₹",
      XOF: "CFA",
    };
    return symbols[currency] || "$";
  };

  // Mobile card layout component
  const CaseCard = ({ caseItem }: { caseItem: Case }) => {
    const client = clients.find((c) => c.id === caseItem.customer_id);
    const progress = getCaseProgressFromStatus(caseItem.status);

    return (
      <Card sx={{ mb: 2, border: "1px solid #e0e0e0" }}>
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
                {caseItem.subject}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {client ? `${client.company}` : t("cases.unknownClient")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1, alignItems: "center" }}>
                <Chip label={caseItem.type} size="small" variant="outlined" />
                <StatusBadge status={caseItem.status} />
                <Chip
                  label={t(`common.${caseItem.priority || "medium"}`)}
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: 220 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ flex: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {progress}%
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Tooltip title={t("common.view")}>
                <IconButton
                  onClick={() => navigate(`/cases/${caseItem.id}`)}
                  size="small"
                >
                  <ViewIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("common.edit")}>
                <IconButton
                  onClick={() => handleOpenDialog(caseItem)}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("common.delete")}>
                <IconButton
                  onClick={() => handleDelete(caseItem.id)}
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {caseItem.lawyers_list
                ?.map((l: any) => l.full_name)
                .join(", ") ||
                (Array.isArray(caseItem.lawyers_assigned)
                  ? caseItem.lawyers_assigned.join(", ")
                  : caseItem.lawyers_assigned)}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {getCurrencySymbol(caseItem.currency)}
              {caseItem.fee}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"}>
          {t("navigation.cases")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size={isMobile ? "small" : "medium"}
          fullWidth={isMobile}
        >
          {t("cases.addCase")}
        </Button>
      </Box>

      <Tabs
        value={statusFilter}
        onChange={(_, value) => setStatusFilter(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none" } }}
      >
        <Tab value="all" label={t("common.all", "All")} />
        {getStatusFilterOptions(t).map((option) => (
          <Tab key={option.value} value={option.value} label={option.label} />
        ))}
      </Tabs>

      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: { xs: 1.5, sm: 2 }, display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            fullWidth
            placeholder={t("cases.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            size={isMobile ? "small" : "medium"}
          />
          {!isMobile && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <ToggleButton value="list">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="kanban">
                <ViewKanbanIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Card>

      {/* Responsive Data Display */}
      {isMobile ? (
        <Box>
          {filteredCases.length === 0 ? (
            <Card sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                {loading ? t("common.loading") : t("cases.noCasesFound")}
              </Typography>
            </Card>
          ) : (
            filteredCases?.map((caseItem) => (
              <CaseCard key={caseItem.id} caseItem={caseItem} />
            ))
          )}
        </Box>
      ) : viewMode === "kanban" ? (
        <CasesKanban
          cases={filteredCases}
          clients={clients}
          onView={(caseItem) => navigate(`/cases/${caseItem.id}`)}
        />
      ) : (
        <Card sx={{ overflowX: "auto" }}>
          <DataGrid
            rows={filteredCases}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            checkboxSelection
            disableRowSelectionOnClick
            autoHeight
            loading={loading}
            onRowClick={(params) => navigate(`/cases/${params.row.id}`)}
            sx={{
              minWidth: 1100,
              border: "none",
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.grey[50],
              },
            }}
          />
        </Card>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {selectedCase ? t("cases.editCase") : t("cases.addCase")}
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Tabs
          value={dialogTab}
          onChange={(e, newValue) => setDialogTab(newValue)}
          sx={{ px: 2 }}
        >
          <Tab label={t("cases.caseDetails")} />
          {selectedCase && <Tab label={t("cases.proformaQuotations")} />}
        </Tabs>
        <DialogContent dividers>
          {dialogTab === 0 ? (
            <Stack spacing={2}>
              <TextField
                select
                label={t("cases.client")}
                required
                fullWidth
                value={formData.customer_id}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    customer_id: parseInt(e.target.value, 10),
                  });
                  if (formErrors.customer_id) {
                    setFormErrors((prev) => ({ ...prev, customer_id: "" }));
                  }
                }}
                error={!!formErrors.customer_id}
                helperText={formErrors.customer_id}
              >
                {clients?.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.company}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label={t("cases.caseType")}
                fullWidth
                required
                value={formData.type}
                onChange={(e) => {
                  setFormData({ ...formData, type: e.target.value });
                  if (formErrors.type) {
                    setFormErrors((prev) => ({ ...prev, type: "" }));
                  }
                }}
                error={!!formErrors.type}
                helperText={formErrors.type}
              >
                <MenuItem value="Arbitration">{t("cases.typeArbitration")}</MenuItem>
                <MenuItem value="Banking and Finance">
                  {t("cases.typeBankingAndFinance")}
                </MenuItem>
                <MenuItem value="Project Finance">{t("cases.typeProjectFinance")}</MenuItem>
                <MenuItem value="Capital Markets">{t("cases.typeCapitalMarkets")}</MenuItem>
                <MenuItem value="Competition and Antitrust">
                  {t("cases.typeCompetitionAndAntitrust")}
                </MenuItem>
                <MenuItem value="Construction Law">{t("cases.typeConstructionLaw")}</MenuItem>
                <MenuItem value="Corporate Law">{t("cases.typeCorporateLaw")}</MenuItem>
                <MenuItem value="Corporate Governance">
                  {t("cases.typeCorporateGovernance")}
                </MenuItem>
                <MenuItem value="Employment">{t("cases.typeEmployment")}</MenuItem>
                <MenuItem value="Oil, Gas and Mining">
                  {t("cases.typeOilGasAndMining")}
                </MenuItem>
                <MenuItem value="Energy">{t("cases.typeEnergy")}</MenuItem>
                <MenuItem value="Franchising">{t("cases.typeFranchising")}</MenuItem>
                <MenuItem value="Immigration">{t("cases.typeImmigration")}</MenuItem>
                <MenuItem value="Intellectual Property and Technology">
                  {t("cases.typeIntellectualPropertyAndTechnology")}
                </MenuItem>
                <MenuItem value="Litigation and Dispute Resolution">
                  {t("cases.typeLitigationAndDisputeResolution")}
                </MenuItem>
                <MenuItem value="Tax">{t("cases.typeTax")}</MenuItem>
                <MenuItem value="Transportation">{t("cases.typeTransportation")}</MenuItem>
              </TextField>

              <TextField
                select
                label={t("cases.priority", "Priority")}
                fullWidth
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
              >
                <MenuItem value="low">{t("common.low")}</MenuItem>
                <MenuItem value="medium">{t("common.medium")}</MenuItem>
                <MenuItem value="high">{t("common.high")}</MenuItem>
              </TextField>

              <TextField
                required
                label={t("cases.caseDescription")}
                fullWidth
                value={formData.subject}
                onChange={(e) => {
                  setFormData({ ...formData, subject: e.target.value });
                  if (formErrors.subject) {
                    setFormErrors((prev) => ({ ...prev, subject: "" }));
                  }
                }}
                error={!!formErrors.subject}
                helperText={formErrors.subject}
              />

              <TextField
                select
                label={t("cases.billingType")}
                fullWidth
                required
                value={formData.billing_type}
                onChange={(e) => {
                  setFormData({ ...formData, billing_type: e.target.value });
                  if (formErrors.billing_type) {
                    setFormErrors((prev) => ({ ...prev, billing_type: "" }));
                  }
                }}
                error={!!formErrors.billing_type}
                helperText={formErrors.billing_type}
              >
                <MenuItem value="hourly">{t("cases.hourly")}</MenuItem>
                <MenuItem value="fixed">{t("cases.fixed")}</MenuItem>
              </TextField>

              {/* Supervisor and Lawyers Assignment - Available for All Billing Types */}

              <TextField
                select
                label={t("cases.currency")}
                fullWidth
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
                <MenuItem value="INR">INR</MenuItem>
                <MenuItem value="XOF">XOF</MenuItem>
              </TextField>

              {/* Supervisor - Available for All Billing Types. Optional (a
                  case can be staffed by lawyers alone — see validateForm's
                  own comment on this), so no `required` marker, and an
                  explicit empty option so a supervisor already picked can
                  actually be cleared again — the real MenuItems alone never
                  offered a way back to "none". */}
              <TextField
                select
                label={t("cases.supervisor")}
                fullWidth
                value={formData.supervisor}
                onChange={(e) => {
                  setFormData({ ...formData, supervisor: e.target.value });
                  if (formErrors.supervisor) {
                    setFormErrors((prev) => ({ ...prev, supervisor: "" }));
                  }
                }}
                error={!!formErrors.supervisor}
                helperText={formErrors.supervisor}
              >
                <MenuItem value="">
                  <em>{t("common.none")}</em>
                </MenuItem>
                {users
                  .filter(
                    (userItem) =>
                      userItem.status === "active" &&
                      userItem.firm_id === user?.firm_id &&
                      userItem.role.name !== "client",
                  )
                  .map((userItem) => (
                    <MenuItem key={userItem.id} value={userItem.user_id}>
                      {userItem.full_name}
                    </MenuItem>
                  ))}
              </TextField>

              {/* Supervisor Fee - Only for Hourly Billing */}
              {formData.billing_type === "hourly" && (
                <TextField
                  label={t("cases.supervisorFee")}
                  type="number"
                  fullWidth
                  // Chrome/Firefox treat a mouse-wheel scroll over a
                  // *focused* number input as an up/down step — including
                  // incidental scrolling, e.g. scrolling this long dialog
                  // down to reach Save right after typing the fee. Blurring
                  // on wheel lets the scroll pass through to the page
                  // instead of silently changing the value.
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                  value={formData.supervisor_fee}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      supervisor_fee: e.target.value,
                    });
                    if (formErrors.supervisor_fee) {
                      setFormErrors((prev) => ({
                        ...prev,
                        supervisor_fee: "",
                      }));
                    }
                  }}
                  error={!!formErrors.supervisor_fee}
                  helperText={formErrors.supervisor_fee}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {getCurrencySymbol(formData.currency)}
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Lawyers Assignment - Available for All Billing Types */}
              {/* For Fixed billing: show lawyers but without fees */}
              {/* For Hourly billing: show lawyers with fees */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {t("cases.lawyersAssigned")}
                </Typography>
                {formData.lawyers_assigned.map((lawyer, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      select
                      label={t("cases.lawyer")}
                      fullWidth
                      value={lawyer.lawyer_id}
                      onChange={(e) =>
                        updateLawyerAssignment(
                          index,
                          "lawyer_id",
                          e.target.value,
                        )
                      }
                    >
                      {users
                        .filter(
                          (userItem) =>
                            userItem.status === "active" &&
                            userItem.role.name !== "client",
                        )
                        .map((userItem) => (
                          <MenuItem key={userItem.id} value={userItem.user_id}>
                            {userItem.full_name}
                          </MenuItem>
                        ))}
                    </TextField>
                    {formData.billing_type === "hourly" && (
                      <TextField
                        label={t("cases.fee")}
                        type="number"
                        sx={{ width: 320 }}
                        // See the supervisor fee field above for why.
                        onWheel={(e) => (e.target as HTMLElement).blur()}
                        value={lawyer.fee}
                        onChange={(e) =>
                          updateLawyerAssignment(index, "fee", e.target.value)
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {getCurrencySymbol(formData.currency)}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    {/* No minimum to enforce here — validateForm's own
                        comment says "having no lawyers assigned at all is
                        allowed", but this button was disabled whenever
                        exactly one lawyer remained (the most common case
                        right after adding the first one), so it could never
                        actually be removed. */}
                    <IconButton
                      onClick={() => removeLawyerAssignment(index)}
                      color="error"
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addLawyerAssignment}
                  variant="outlined"
                  sx={{ mt: 1 }}
                >
                  {t("cases.addLawyer")}
                </Button>
                {formErrors.lawyers_assigned && (
                  <FormHelperText error>
                    {formErrors.lawyers_assigned}
                  </FormHelperText>
                )}
              </Box>

              {/* Conditional Fee Fields Based on Billing Type */}
              {formData.billing_type === "fixed" && (
                <TextField
                  label={t("cases.fixedFee")}
                  type="number"
                  fullWidth
                  // See the supervisor fee field above for why.
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                  value={formData.fixed_fee}
                  onChange={(e) => {
                    setFormData({ ...formData, fixed_fee: e.target.value });
                    if (formErrors.fixed_fee) {
                      setFormErrors((prev) => ({ ...prev, fixed_fee: "" }));
                    }
                  }}
                  error={!!formErrors.fixed_fee}
                  helperText={formErrors.fixed_fee}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {getCurrencySymbol(formData.currency)}
                      </InputAdornment>
                    ),
                  }}
                  required
                />
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label={t("cases.startDate")}
                  type="date"
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.start_date}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                />

                <TextField
                  label={t("cases.endDate")}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                />
              </Box>

              {/* Public Access Section */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {t("cases.publicCaseAccess")}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enable_public_access}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handlePublicAccessToggle(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={t("cases.enablePublicAccessDescription")}
                />

                {formData.enable_public_access &&
                  formData.public_access_code && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>{t("cases.accessCodeLabel")}</strong>{" "}
                          {formData.public_access_code}
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          Share this access code with your client to allow them
                          to view their case details publicly.
                        </Typography>
                      </Alert>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Access code will be available after case creation
                        </Typography>
                        <Chip
                          label={formData.public_access_code}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>

                      {/* Show public access link input and copy button when link is available */}
                      {publicAccessLink && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontWeight: "bold" }}
                          >
                            Public Access Link:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <TextField
                              fullWidth
                              value={publicAccessLink}
                              inputProps={{ readOnly: true }}
                              size="small"
                              sx={{
                                "& .MuiInputBase-input.MuiInputBase-readOnly": {
                                  backgroundColor: "grey.100",
                                },
                              }}
                            />
                            <Button
                              startIcon={<CopyIcon />}
                              onClick={copyPublicAccessLink}
                              variant="outlined"
                              size="small"
                            >
                              {linkCopied
                                ? t("common.copied")
                                : t("common.copy")}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
              </Box>
            </Stack>
          ) : (
            selectedCase && (
              <ProformaQuotation
                caseId={selectedCase?.id}
                clientId={selectedCase?.customer_id}
                billingType={selectedCase?.billing_type}
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedCase ? t("cases.updateCase") : t("cases.createCase")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Public Access Link Display */}
      {publicAccessLink && (
        <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            {t("cases.publicAccessLinkGenerated")}
          </Typography>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t("cases.caseCreatedSuccess")}
            </Typography>
          </Alert>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ flex: 1, wordBreak: "break-all" }}
            >
              {publicAccessLink}
            </Typography>
            <Button
              startIcon={<CopyIcon />}
              onClick={copyPublicAccessLink}
              variant="outlined"
              size="small"
            >
              {linkCopied ? t("common.copied") : t("common.copy")}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Cases;
